import React, { useState, useEffect } from 'react';
import {
  Breadcrumb,
  Button,
  Card,
  Col,
  Divider,
  Empty,
  Input,
  Layout,
  Result,
  Row,
  Skeleton,
  Table,
  notification,
  theme,
  Statistic,
  Tag
} from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { apiGetManagedAccountVirtualAccountDetail, apiGetManagedVirtualAccountEntries, apiGetVirtualAccountDetails, apiGetVirtualAccountEntries } from "../../../../api/virtualAccounts";
import { formatDateTime, formatPrice } from "../../../../utils/format";
import { useIsMobileHook } from "../../../../utils/WindowHooks";
import "./VirtualAccountDetails.scss";

const { Content } = Layout;
const { Search } = Input;

const VirtualAccountDetails = () => {
  const { id, managedAccountId } = useParams();
  const [accountDetails, setAccountDetails] = useState(null);
  const [entries, setEntries] = useState([]);
  const [isDetailsFetching, setIsDetailsFetching] = useState(false);
  const [isEntriesFetching, setIsEntriesFetching] = useState(false);
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const isMobile = useIsMobileHook(1600);
  const [pagination, setPagination] = useState({
    current: 1,
    total: 0,
  });
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [activeError, setActiveError] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");

  const handleRowClick = (record) => {
    const entryId = record.id;
    const newExpandedRowKeys = [...expandedRowKeys];
    const index = newExpandedRowKeys.indexOf(entryId);
    if (index > -1) {
      newExpandedRowKeys.splice(index, 1);
    } else {
      newExpandedRowKeys.push(entryId);
    }
    setExpandedRowKeys(newExpandedRowKeys);
  };

  const fetchAccountDetails = async () => {
    setIsDetailsFetching(true);
    try {
      const data = managedAccountId
        ? await apiGetManagedAccountVirtualAccountDetail(managedAccountId, id)
        : await apiGetVirtualAccountDetails(id);
      setAccountDetails(data);
    } catch (error) {
      setActiveError(true);
      setErrorStatus(error.status);
      notification.error({
        message: "Erro",
        description: "Ocorreu um erro ao buscar os detalhes da conta.",
        placement: "topRight",
      });
    } finally {
      setIsDetailsFetching(false);
    }
  };

  const fetchEntries = async (params) => {
    setIsEntriesFetching(true);
    try {
      const filteredParams = Object.fromEntries(
        Object.entries(params).filter(([_, value]) => value !== undefined && value !== null && value !== '')
      );
      const json = managedAccountId
        ? await apiGetManagedVirtualAccountEntries(id, filteredParams)
        : await apiGetVirtualAccountEntries(id, filteredParams);
      setEntries(json.results || []);
      setPagination((prevPagination) => ({
        ...prevPagination,
        total: json.count,
        current: params.page || 1,
      }));
    } catch (error) {
      setActiveError(true);
      setErrorStatus(error.status);
      notification.error({
        message: "Erro",
        description: "Ocorreu um erro ao buscar as movimentações.",
        placement: "topRight",
      });
    } finally {
      setIsEntriesFetching(false);
    }
  };

  const onChangePage = (paginationParams) => {
    fetchEntries({
      page: paginationParams.current,
      search: searchText || undefined,
    });
  };

  const onSearchEntries = (value) => {
    setSearchText(value);
    setPagination({ current: 1, total: 0 });
    fetchEntries({
      page: 1,
      search: value || undefined,
    });
  };

  const backPath = managedAccountId
    ? `/conta-gerenciada/${managedAccountId}`
    : "/contas-virtuais";

  useEffect(() => {
    if (id) {
      fetchAccountDetails();
      fetchEntries({ page: 1 });
    }
  }, [id, managedAccountId]);

  const accountTypeMap = {
    SUB_SELLER_PF: "Pessoa Física",
    SUB_SELLER_PJ: "Pessoa Jurídica",
    SELLER_PF: "Pessoa Física",
    SELLER_PJ: "Pessoa Jurídica",
  };

  const renderAccountType = (type) => {
    return accountTypeMap[type] || "Indefinido";
  };

  const kindMap = {
    IN: <Tag color="green">Entrada</Tag>,
    OUT: <Tag color="red">Saída</Tag>,
  };

  const renderKind = (kind) => {
    return kindMap[kind] || <Tag>Indefinido</Tag>;
  };

  const statusMap = {
    PAID: <span className="badge badge-success">Pago</span>,
    PENDING: <span className="badge yellow">Pendente</span>,
    REVERSED: <span className="badge badge-error">Estornado</span>,
    FAILED: <span className="badge badge-error">Falhou</span>,
  };

  const renderStatus = (status) => {
    return statusMap[status] || <span className="badge black">Indefinido</span>;
  };

  const columns = [
    {
      title: "Data/Hora",
      dataIndex: "created_at",
      key: "created_at",
      onCell: () => ({
        style: { cursor: "pointer" },
      }),
      render: (created_at) => (
        <p
          style={{ textTransform: "capitalize" }}
        >
          {formatDateTime(created_at)}
        </p>
      ),
    },
    {
      title: "Tipo",
      dataIndex: "kind",
      key: "kind",
      render: renderKind,
      responsive: ["sm"],
    },
    {
      title: "Descrição",
      dataIndex: "name",
      key: "name",
      responsive: ["md"],
    },
    {
      title: "Valor",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => (
        <p
          style={{ textTransform: "capitalize" }}
        >
          {formatPrice(amount / 100)}
        </p>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: renderStatus,
      responsive: ["sm"],
    },
  ];

  const expandedRowRender = (record) => (
    <div className="expandid">
      <span>
        Data/Hora:{" "}
        <p>{formatDateTime(record.created_at)}</p>
      </span>
      <span>
        Tipo:{" "}
        {renderKind(record.kind)}
      </span>
      <span>
        Descrição:{" "}
        <p>{record.name}</p>
      </span>
      <span>
        Valor:{" "}
        <p>{formatPrice(record.amount / 100)}</p>
      </span>
      <span>
        Status:{" "}
        {renderStatus(record.status)}
      </span>
      {record.status_reason && (
        <span>
          Motivo:{" "}
          <p>{record.status_reason}</p>
        </span>
      )}
    </div>
  );

  const locale = {
    emptyText: <Empty description="Sem dados" />,
  };

  const renderError = (status) => {
    if (activeError) {
      return (
        <Result
          status={status === "500" ? "500" : "404"}
          title={status}
          subTitle={
            status === "500"
              ? "Ocorreu um erro no servidor, tente novamente mais tarde."
              : "Ocorreu um erro, tente novamente mais tarde."
          }
          extra={
            <Button onClick={() => navigate(backPath)} type="primary">
              Voltar para Contas Virtuais
            </Button>
          }
        />
      );
    }
  };

  return (
    <div className="VirtualAccountDetails">
      <Layout
        style={{
          padding: "1.25rem",
        }}
      >
        <Breadcrumb
          style={{
            margin: "16px 0",
          }}
        >
          <div style={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center" }}>
            <h2 className="title-page">Detalhes da Conta Virtual</h2>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(backPath)}
            >
              Voltar
            </Button>
          </div>
          <Divider />
          <Breadcrumb.Item>Contas Virtuais</Breadcrumb.Item>
          <Breadcrumb.Item>Detalhes</Breadcrumb.Item>
        </Breadcrumb>
        <Content
          style={{
            padding: 24,
            margin: 0,
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          {isDetailsFetching ? (
            <Skeleton active />
          ) : activeError ? (
            renderError(errorStatus)
          ) : (
            <>
              {/* Account Details Cards */}
              <Row gutter={[24, 24]} className="account-details-row" style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic
                      title="Nome da Conta"
                      value={accountDetails?.account?.display_name || "N/A"}
                      valueStyle={{ fontSize: '18px' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic
                      title="Tipo"
                      value={accountDetails?.account?.type ? renderAccountType(accountDetails.account.type) : "N/A"}
                      valueStyle={{ fontSize: '18px' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic
                      title="Saldo Atual"
                      value={accountDetails?.balance ? (accountDetails.balance / 100).toFixed(2) : "0.00"}
                      prefix="R$"
                      valueStyle={{ fontSize: '18px', color: '#3f8600' }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card>
                    <Statistic
                      title="Data de Criação"
                      value={accountDetails?.created_at ? new Date(accountDetails.created_at).toLocaleDateString('pt-BR') : "N/A"}
                      valueStyle={{ fontSize: '18px' }}
                    />
                  </Card>
                </Col>
              </Row>

              <Divider />
              <h3 className="subtitle-page">Movimentações</h3>

              <div style={{ marginBottom: 16, marginTop: 16 }}>
                <Search
                  placeholder="Buscar movimentação"
                  allowClear
                  enterButton="Buscar"
                  onSearch={onSearchEntries}
                  style={{ width: 300 }}
                />
              </div>
              {isEntriesFetching ? (
                <Skeleton active />
              ) : (
                <Table
                  columns={columns}
                  locale={locale}
                  expandable={{
                    expandedRowRender: isMobile ? expandedRowRender : undefined,
                    expandedRowKeys: expandedRowKeys,
                    onExpand: (_expanded, record) => handleRowClick(record),
                    responsive: ["xl"],
                  }}
                  dataSource={
                    entries &&
                    entries.map((item) => ({ ...item, key: item.id }))
                  }
                  pagination={{
                    current: pagination.current,
                    total: pagination.total,
                    showSizeChanger: false,
                  }}
                  bordered
                  onChange={onChangePage}
                />
              )}
            </>
          )}
        </Content>
      </Layout>
    </div>
  );
};

export default VirtualAccountDetails;
